import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { MediBlogCategory, slugify } from "../../../../services/mediBlog";

interface MediBlogCategoryDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  editingCategory: MediBlogCategory | null;
  setEditingCategory: React.Dispatch<React.SetStateAction<MediBlogCategory | null>>;
}

const MediBlogCategoryDialog: React.FC<MediBlogCategoryDialogProps> = ({
  open,
  onClose,
  onSave,
  editingCategory,
  setEditingCategory,
}) => {
  const { t } = useTranslation();

  const handleChange = (field: keyof MediBlogCategory, value: unknown) => {
    if (!editingCategory) return;
    const next = { ...editingCategory, [field]: value };
    if (field === "name_en" && typeof value === "string" && !editingCategory.id) {
      next.slug = slugify(value);
    }
    setEditingCategory(next);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {editingCategory?.id
          ? t("admin.contentManagement.mediblog.editCategory")
          : t("admin.contentManagement.mediblog.addCategory")}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label={t("admin.contentManagement.fields.titleEn")}
            fullWidth
            required
            value={editingCategory?.name_en || ""}
            onChange={(e) => handleChange("name_en", e.target.value)}
          />
          <TextField
            label={t("admin.contentManagement.fields.titleAr")}
            fullWidth
            value={editingCategory?.name_ar || ""}
            onChange={(e) => handleChange("name_ar", e.target.value)}
          />
          <TextField
            label={t("admin.contentManagement.fields.titleFr")}
            fullWidth
            value={editingCategory?.name_fr || ""}
            onChange={(e) => handleChange("name_fr", e.target.value)}
          />
          <TextField
            label={t("admin.contentManagement.mediblog.slug")}
            fullWidth
            required
            value={editingCategory?.slug || ""}
            onChange={(e) => handleChange("slug", slugify(e.target.value))}
            helperText={t("admin.contentManagement.mediblog.slugHelper")}
          />
          <TextField
            label={t("admin.contentManagement.fields.description")}
            fullWidth
            multiline
            rows={3}
            value={editingCategory?.description || ""}
            onChange={(e) => handleChange("description", e.target.value)}
          />
          <TextField
            label={t("admin.contentManagement.fields.displayOrder")}
            fullWidth
            type="number"
            value={editingCategory?.display_order ?? 0}
            onChange={(e) => handleChange("display_order", parseInt(e.target.value, 10) || 0)}
          />
          <FormControlLabel
            control={
              <Switch
                checked={editingCategory?.is_active ?? true}
                onChange={(e) => handleChange("is_active", e.target.checked)}
              />
            }
            label={t("admin.contentManagement.active")}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("admin.contentManagement.cancel")}</Button>
        <Button variant="contained" onClick={onSave} disabled={!editingCategory?.name_en || !editingCategory?.slug}>
          {t("admin.contentManagement.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MediBlogCategoryDialog;
